import PackingSlip from "../../../classes/PackingSlip";
import Sale from "../../../classes/Sale";
import SaleLabor from "../../../classes/SaleLabor";
import { dedupeItemLocations } from "../../../classes/ItemLocation";
import { isPickupAddressAtWarehouse } from "../../../util/pickupLocations";
import { slipReceivedUnits } from "../../../util/receiving";
import { db } from "../../../boot/firebase";
import { call } from "../../../util/misc";
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  increment,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { cloneDeep, isBoolean, isEmpty, keyBy } from "lodash";

/**
 * Compute the clamped per-item `quantityReady` delta and updated
 * itemLocation list for a QC packing-slip inspection.
 *
 * The raw QC inspection semantics are additive: whatever the inspector
 * reports as `ready` for an item gets added to the item's existing
 * `quantityReady`, and each reported location quantity gets added to
 * any matching existing location. That's correct when the item starts
 * in a "no QC done yet" state — but when the inspector creates (or
 * edits) the sale item mid-QC via SaleItemEdit and pre-populates
 * `quantityReady` plus a warehouse location, the additive submission
 * then doubles both fields. Sale 01400022B ran into this: four
 * accessory items ended up with `quantityReady = 2 × quantity`, the
 * scheduled delivery event inherited the inflated count via
 * `EventEdit.vue`'s `quantityReady - quantityScheduled`, and `repairSale`
 * later wrote `status.delivery.scheduled = 27` against `total.items = 16`.
 *
 * This helper clamps `newlyReady` so `currentReady + newlyReady` never
 * exceeds `item.quantity`, then distributes the increment across the
 * reported locations greedily in their reported order, separately
 * bounded so the location snapshot never exceeds `item.quantity`
 * either. The two bounds are deliberately INDEPENDENT — see the
 * implementation comments for why conflating them silently voided
 * legitimate receipts.
 *
 * @param {object} params
 * @param {{quantity:number, quantityReady:number}} params.item
 *   Sale item data (subset). `item.quantity` must be a non-negative
 *   number; `item.quantityReady` defaults to 0 if missing.
 * @param {number} params.reportedReady - `itemStatus.ready` from the QC
 *   packing slip (sum of per-location reported quantities).
 * @param {Array<{type,id,itemType,bin,quantity,...}>} params.reportedLocations
 *   `itemStatus.locations` from the QC packing slip.
 * @param {Array<object>} params.existingLocations - Current entries on
 *   the item's most-recent `itemLocation` (deep-cloned internally, not
 *   mutated).
 * @returns {{
 *   newlyReady: number,
 *   quantityReady: number,
 *   itemLocation: Array<object>,
 * }} `newlyReady` is the clamped ready delta (0 when the item is already
 *   at capacity), `quantityReady` is the post-update total, and
 *   `itemLocation` is the updated (not yet deduped) location list, whose
 *   total may grow by less than `newlyReady` when the snapshot is already
 *   at capacity.
 */
export function computeQcReadyUpdate({
  item,
  reportedReady,
  reportedLocations,
  existingLocations,
}) {
  const currentReady = item.quantityReady || 0;
  const itemQuantity = item.quantity || 0;
  const itemLocation = existingLocations.map((l) => ({ ...l }));

  // `quantityReady` is bounded ONLY by the remaining ready capacity. This
  // stops the additive double-count (sale 01400022B: an item created mid-QC
  // with quantityReady already at quantity yields newlyReady=0) while leaving
  // every genuine receipt creditable.
  const newlyReady = Math.max(
    0,
    Math.min(reportedReady || 0, itemQuantity - currentReady)
  );
  const quantityReady = currentReady + newlyReady;

  // The location snapshot is bounded SEPARATELY, by its own remaining
  // capacity: an item's physical `itemLocations` can never represent more
  // units than were sold. Sale 04000015A's `ewLS` accessory needs this —
  // seeded at `[AA8:1]` with quantityReady=0, the QC reported `AA8:1 + S93:1`
  // on top, which would double the snapshot to 3 (then 4 on a re-run) for a
  // quantity-2 item.
  //
  // These two bounds must NOT be combined into a single `newlyReady`. Doing so
  // assumes `existingTotal === quantityReady`, and `adjustQuantityReady`
  // (store/main/actions/issues.js) breaks that invariant every time an ad-hoc
  // `orderRequired` damage issue decrements `quantityReady` without touching
  // `itemLocations`. Once the original units also sit in a final
  // (delivered/installed) location, `existingTotal` still equals `quantity`,
  // so the replacement order's QC computed `min(reported, 1, 0) = 0` and
  // credited NOTHING: the slip read "Received complete" while `quantityReady`
  // stayed short, `computeVendorFullyReceived` stayed false, and the order,
  // its acks, `delivery.ready` and `install.readyItems` were all stranded —
  // pinning a delivered, installed and paid sale at "production" (sale
  // 03900102A, item `Zklqmx41MlTGkJ9Kszci`). Bounding the two independently
  // credits the receipt while still refusing to overfill the snapshot.
  const existingTotal = itemLocation.reduce(
    (sum, l) => sum + (Number(l.quantity) || 0),
    0
  );
  const locationCapacity = Math.max(0, itemQuantity - existingTotal);

  if (newlyReady > 0) {
    let remainingToDistribute = Math.min(newlyReady, locationCapacity);
    for (const location of reportedLocations || []) {
      if (remainingToDistribute <= 0) break;
      const reportedQty = location.quantity || 0;
      if (reportedQty <= 0) continue;
      const add = Math.min(reportedQty, remainingToDistribute);
      remainingToDistribute -= add;
      const existingLocation = itemLocation.find(
        (l) =>
          l.type === location.type &&
          l.id === location.id &&
          l.itemType === location.itemType &&
          l.bin === location.bin
      );
      if (existingLocation) {
        existingLocation.quantity += add;
      } else {
        itemLocation.push({ ...location, quantity: add });
      }
    }
  }

  return { newlyReady, quantityReady, itemLocation };
}

/**
 * Determine whether a vendor's entire portion of a sale is fully received,
 * derived from the vendor's sale items (quantityReady vs quantity).
 *
 * This is the authoritative receipt signal: sale items carry only `brand` (the
 * vendor) with no order/ack reference, so once every one of the vendor's
 * items is ready the material can't be attributed to a single order — every
 * order and ack for that vendor is therefore received. Deliberately quantity-
 * based rather than driven by the per-slip `receivedComplete` checkbox: a
 * single slip mistakenly marked complete must not be able to mark a whole
 * order received while items are objectively outstanding. The only other way
 * a flag is raised is an explicit per-ack `receivedOverride` (the super-only
 * order-level "Fully Received" backstop) for material that will never ship.
 *
 * @param {object} params
 * @param {Array<{quantity:number, quantityReady:number}>} params.items
 *   Sale items for the vendor, with `quantityReady` already reflecting any
 *   per-item updates applied in the current (not-yet-committed) save.
 * @returns {boolean} Whether every relevant item is fully received. Returns
 *   false when there are no items with a positive quantity (no evidence), so
 *   a flag is never set true without evidence.
 */
export function computeVendorFullyReceived({ items }) {
  const relevant = (items || []).filter((i) => (i.quantity || 0) > 0);
  return (
    relevant.length > 0 &&
    relevant.every((i) => (i.quantityReady || 0) >= i.quantity)
  );
}

/**
 * Build the `sale.status` dot-path update map applied when a packing slip is
 * saved/QC'd.
 *
 * Receipt completeness is tracked per acknowledgement (see
 * `computeOrderReceipt`) and rolled up to the order as the AND of its acks
 * — matching `repairSale`, which treats `sale.status.acks[*]` as the source of
 * truth and aggregates orders the same way, and `computeSaleStatus`, which
 * advances the sale past "production" only when every live ack is received.
 * The saved slip's own counters (`qcInspected`/`included`/`ready`) are always
 * written.
 *
 * So is what the slip says came off the truck, which sale items only show
 * once QC makes them ready: `receivedComplete` (the slip covers everything
 * the order is owed -- computeSaleStatus reads it to say "Quality Control"
 * rather than "In production" while the slip waits for QC) and `received`
 * (units per sale item, slipReceivedUnits -- the item's Received count). The
 * latter is passed only until QC: the slip's `items` map is QC's report after
 * that, and the receiving-time count is what stays.
 *
 * @param {object} params
 * @param {string} params.packingSlipID - The saved slip's id.
 * @param {boolean} params.qcInspected - The saved slip's QC flag.
 * @param {?{included:number, ready:number}} params.total - The slip's totals
 *   (null/undefined coerces the counters to 0).
 * @param {Object<string,boolean>} params.ackReceived - Per-ack `fullyReceived`
 *   keyed by ackID for every ack on the saved slip's order.
 * @param {Object<string,boolean>} params.orderReceived - Per-order
 *   `fullyReceived` keyed by orderID (the order-level AND rollup).
 * @param {boolean} [params.receivedComplete] - The slip's own flag; left
 *   unwritten when omitted.
 * @param {Object<string,number>} [params.received] - Units per sale item;
 *   left unwritten when omitted.
 * @returns {object} Firestore dot-path changes for UPDATE_SALE_STATUS.
 */
export function buildReceivedStatusUpdates({
  packingSlipID,
  qcInspected,
  total,
  ackReceived,
  orderReceived,
  receivedComplete,
  received,
}) {
  const updates = {
    [`status.packingSlips.${packingSlipID}.qcInspected`]: qcInspected,
    [`status.packingSlips.${packingSlipID}.included`]: total
      ? total.included
      : 0,
    [`status.packingSlips.${packingSlipID}.ready`]: total ? total.ready : 0,
  };
  if (receivedComplete !== undefined) {
    updates[`status.packingSlips.${packingSlipID}.receivedComplete`] =
      receivedComplete === true;
  }
  if (received !== undefined) {
    updates[`status.packingSlips.${packingSlipID}.received`] = received;
  }
  Object.entries(ackReceived || {}).forEach(([id, value]) => {
    updates[`status.acks.${id}.fullyReceived`] = value;
  });
  Object.entries(orderReceived || {}).forEach(([id, value]) => {
    updates[`status.orders.${id}.fullyReceived`] = value;
  });
  return updates;
}

/**
 * Whether a re-order has received everything it was placed for.
 *
 * A re-order carries the issues it replaces (`order.issues`), and each issue
 * names the item and quantity owed. Unlike sale items, that IS attributable to
 * one order, so the re-order need not wait on unrelated shortfalls elsewhere
 * in the vendor (sale 01100014B: a 4-screen re-order stuck behind a damaged
 * unit on the original order). Quantity-based like
 * `computeVendorFullyReceived`: only QC'd slips count, using their `ready`
 * units, and the `receivedComplete` checkbox is not an input.
 *
 * @param {object} params
 * @param {Array<?{itemID:string, quantity:number}>} params.issues - Data of
 *   every issue on the order (null for one that no longer exists).
 * @param {Array<{qcInspected:boolean, items:object}>} params.packingSlips -
 *   Data of every packing slip on the order.
 * @returns {boolean} False without issues, or when any issue is missing or
 *   lacks an item/quantity, so a flag is never set true without evidence.
 */
export function computeReorderReceived({ issues, packingSlips }) {
  if (!issues || !issues.length) return false;
  const owed = {};
  for (const issue of issues) {
    const quantity = Number(issue && issue.quantity) || 0;
    if (!issue || !issue.itemID || quantity <= 0) return false;
    owed[issue.itemID] = (owed[issue.itemID] || 0) + quantity;
  }
  const ready = {};
  (packingSlips || []).forEach((slip) => {
    if (!slip || !slip.qcInspected) return;
    Object.entries(slip.items || {}).forEach(([itemID, item]) => {
      ready[itemID] = (ready[itemID] || 0) + (Number(item && item.ready) || 0);
    });
  });
  return Object.entries(owed).every(
    ([itemID, quantity]) => (ready[itemID] || 0) >= quantity
  );
}

/**
 * Load an order's issues and packing slips and apply
 * `computeReorderReceived`. Orders without issues skip the reads.
 *
 * @param {Function} dispatch
 * @param {object} params
 * @param {?object} params.order - The order (reads `order.data.issues`).
 * @param {?{id:string, data:object}} [params.pendingSlip] - A slip being
 *   saved in the current (not-yet-committed) batch; replaces its stored copy.
 * @param {string} [params.excludeSlipID] - A slip being deleted.
 * @returns {Promise<boolean>}
 */
async function loadReorderReceived(
  dispatch,
  { projectID, saleID, orderID, order, pendingSlip, excludeSlipID }
) {
  const issueIDs = (order && order.data && order.data.issues) || [];
  if (!issueIDs.length) return false;
  const [issues, slipsSnap] = await Promise.all([
    Promise.all(
      issueIDs.map((issueID) =>
        dispatch("GET_ISSUE", { projectID, saleID, issueID })
      )
    ),
    getDocs(collection(db, PackingSlip.getPath(projectID, saleID, orderID))),
  ]);
  const packingSlips = slipsSnap.docs
    .filter(
      (slip) =>
        slip.id !== excludeSlipID && slip.id !== (pendingSlip && pendingSlip.id)
    )
    .map((slip) => slip.data());
  if (pendingSlip) packingSlips.push(pendingSlip.data);
  return computeReorderReceived({
    issues: issues.map((issue) => (issue && issue.data) || null),
    packingSlips,
  });
}

/**
 * Recompute per-ack and per-order receipt completeness for an order.
 *
 * Each ack is received when the whole vendor is received
 * (`vendorFullyReceived`, the item-quantity signal), when the order is a
 * re-order that has received everything its issues are owed
 * (`reorderReceived`, see `computeReorderReceived`), OR when the ack carries
 * an explicit `receivedOverride` (the super-only manual backstop). The order
 * is the AND of its acks. The per-slip `receivedComplete` checkbox is not an
 * input — it only drives per-shipment backorder/issue display.
 *
 * @param {Iterable<string>} params.ackIDs - Every ack id on the order.
 * @param {string} params.orderID - The order id.
 * @param {boolean} [params.vendorFullyReceived] - Whether the vendor's whole
 *   portion of the sale is received (item-quantity signal).
 * @param {boolean} [params.reorderReceived] - Whether the order is a re-order
 *   whose issue quantities are all received.
 * @param {Object<string,boolean>} [params.ackOverrides] - Per-ack manual
 *   `receivedOverride` keyed by ackID.
 * @returns {{ackReceived: Object<string,boolean>, orderReceived:
 *   Object<string,boolean>}}
 */
export function computeOrderReceipt({
  ackIDs,
  orderID,
  vendorFullyReceived = false,
  reorderReceived = false,
  ackOverrides = {},
}) {
  const ackReceived = {};
  new Set(ackIDs).forEach((id) => {
    ackReceived[id] =
      vendorFullyReceived || reorderReceived || Boolean(ackOverrides[id]);
  });
  const orderReceived = {
    [orderID]:
      Object.keys(ackReceived).length > 0 &&
      Object.values(ackReceived).every(Boolean),
  };
  return { ackReceived, orderReceived };
}

/**
 * Raise-only receipt changes for a vendor whose sale items are all ready:
 * every one of the vendor's orders and their acks is marked received, and an
 * order with no packing slip to inspect is marked QC'd as well — the rule
 * `markOrderFullyReceived` applies — so the vendor tab does not park it at
 * "QC" forever. Entries already `true` are left out, so an empty result means
 * there is nothing to write. Nothing is ever lowered.
 *
 * @param {object} params
 * @param {Iterable<string>} params.vendorOrderIDs - Ids of the vendor's orders.
 * @param {Object<string,object>} [params.statusOrders] - `sale.status.orders`.
 * @param {Object<string,object>} [params.statusAcks] - `sale.status.acks`.
 * @param {Object<string,object>} [params.statusPackingSlips] -
 *   `sale.status.packingSlips`.
 * @returns {Object<string,boolean>} Firestore dot-path changes for
 *   UPDATE_SALE_STATUS.
 */
export function buildVendorReceiptRaise({
  vendorOrderIDs,
  statusOrders = {},
  statusAcks = {},
  statusPackingSlips = {},
}) {
  const orderIDs = new Set(vendorOrderIDs || []);
  const ordersWithSlips = new Set(
    Object.values(statusPackingSlips || {})
      .map((slip) => slip && slip.orderID)
      .filter(Boolean)
  );
  const changes = {};
  orderIDs.forEach((orderID) => {
    const entry = (statusOrders || {})[orderID];
    if (!entry) return;
    if (entry.fullyReceived !== true) {
      changes[`status.orders.${orderID}.fullyReceived`] = true;
    }
    if (!ordersWithSlips.has(orderID) && entry.qcInspected !== true) {
      changes[`status.orders.${orderID}.qcInspected`] = true;
    }
  });
  Object.entries(statusAcks || {}).forEach(([ackID, entry]) => {
    if (entry && orderIDs.has(entry.orderID) && entry.fullyReceived !== true) {
      changes[`status.acks.${ackID}.fullyReceived`] = true;
    }
  });
  return changes;
}

/**
 * Raise the vendor's receipt flags if every one of its sale items is ready.
 *
 * `computeVendorFullyReceived` is the receipt signal (see SAVE_PACKING_SLIP);
 * a packing-slip QC is normally what satisfies it and projects `true` across
 * the vendor's orders and acks. This action re-runs that projection from the
 * other place the signal can flip — an order-required issue handing its units
 * back (`issueHoldsUnits` in issues.js) — so a reorder that never crossed the
 * dock stops pinning the sale at "production". Raise-only: it writes nothing
 * while the vendor still has units outstanding, or when every flag is already
 * up. `touchSale` mirrors the raised `sale.status` entries onto the order and
 * ack documents.
 *
 * @returns {Promise<boolean>} Whether anything was written.
 */
export const RAISE_VENDOR_RECEIPT = async (
  { dispatch },
  { projectID, saleID, vendor }
) => {
  if (!vendor) return false;
  const salePath = `projects/${projectID}/sales/${saleID}`;
  const [itemsSnap, ordersSnap, sale] = await Promise.all([
    getDocs(
      query(collection(db, `${salePath}/items`), where("brand", "==", vendor))
    ),
    getDocs(
      query(collection(db, `${salePath}/orders`), where("vendor", "==", vendor))
    ),
    dispatch("GET_SALE", { projectID, saleID }),
  ]);
  const vendorFullyReceived = computeVendorFullyReceived({
    items: itemsSnap.docs.map((doc) => {
      const data = doc.data();
      return {
        quantity: data.quantity || 0,
        quantityReady: data.quantityReady || 0,
      };
    }),
  });
  if (!vendorFullyReceived) return false;

  const status = (sale && sale.data && sale.data.status) || {};
  const changes = buildVendorReceiptRaise({
    vendorOrderIDs: ordersSnap.docs.map((doc) => doc.id),
    statusOrders: status.orders,
    statusAcks: status.acks,
    statusPackingSlips: status.packingSlips,
  });
  if (isEmpty(changes)) return false;

  await dispatch("UPDATE_SALE_STATUS", { projectID, saleID, changes });
  return true;
};

export const GET_PACKING_SLIP = async (
  { dispatch },
  { projectID, saleID, orderID, packingSlipID }
) => {
  return dispatch("GET_DOC", {
    converter: PackingSlip,
    path: PackingSlip.getPath(projectID, saleID, orderID, packingSlipID),
    stateKey: "packingSlips",
    id: packingSlipID,
  });
};

export const GET_PACKING_SLIPS = (
  { dispatch },
  {
    projectID,
    saleID,
    orderID,
    conditions,
    where,
    limit,
    limitCount,
    pagination,
  }
) => {
  const path =
    projectID && saleID && orderID
      ? PackingSlip.getPath(projectID, saleID, orderID)
      : null;
  // v1.2.1 callers pass where/limit; the branch store API (init.js) reads
  // conditions/limitCount and REQUIRES a colGroup whenever path is null.
  return dispatch("GET_COLLECTION", {
    converter: PackingSlip,
    stateKey: "packingSlips",
    colGroup: !path ? "packing-slips" : null,
    path,
    conditions: conditions ?? where,
    limitCount: limitCount ?? limit,
    pagination,
  });
};

export const LISTEN_PACKING_SLIP = async (
  { dispatch },
  { projectID, saleID, orderID, packingSlipID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: PackingSlip,
    path: PackingSlip.getPath(projectID, saleID, orderID, packingSlipID),
    stateKey: "packingSlips",
    id: packingSlipID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_PACKING_SLIPS = (
  { dispatch },
  {
    projectID,
    saleID,
    orderID,
    conditions,
    where,
    listenerPath,
    error,
    next,
    unlisten,
  }
) => {
  const path =
    projectID && saleID && orderID
      ? PackingSlip.getPath(projectID, saleID, orderID)
      : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: PackingSlip,
    stateKey: "packingSlips",
    colGroup: !path ? "packing-slips" : null,
    listenerPath,
    path,
    conditions: conditions ?? where,
    next,
    error,
    unlisten,
  });
};

export const SAVE_PACKING_SLIP = async (
  { dispatch, state },
  { projectID, saleID, orderID, packingSlip }
) => {
  const batch = writeBatch(db);

  const saveDocuments = (existingData) => {
    return Promise.all([
      dispatch("SAVE_DOCUMENTS", {
        parentData: packingSlip.data,
        fieldPath: "document",
        folderPath: packingSlip.path,
        isSingle: true,
        existingData,
      }),
      dispatch("SAVE_DOCUMENTS", {
        parentData: packingSlip.data,
        fieldPath: "report",
        folderPath: packingSlip.path,
        isSingle: true,
        existingData,
      }),
    ]);
  };

  const updateSale = async (changes = {}, itemReadyOverrides = {}) => {
    if (isBoolean(packingSlip.receivedComplete)) {
      // Receipt completeness is authoritative from item quantities
      // (computeVendorFullyReceived): a vendor is received once every one of
      // its sale items has quantityReady >= quantity. A super can also force
      // it via a persistent per-ack `receivedOverride` (the order-level
      // "Fully Received" backstop) for material that will never fully ship.
      // The per-slip `receivedComplete` checkbox is NOT an input here — it
      // only drives per-shipment backorder/issue display — so one mistakenly
      // "complete" slip can't mark a whole order received while items are
      // objectively outstanding. Per-item QC updates from THIS save aren't
      // committed yet, so overlay their projected quantityReady from
      // `itemReadyOverrides`. A re-order is also received once its own issue
      // quantities are in (computeReorderReceived), counting this slip as
      // saved.
      const [acksSnap, itemsSnap, sale, order] = await Promise.all([
        getDocs(
          collection(
            db,
            `projects/${projectID}/sales/${saleID}/orders/${orderID}/acks`
          )
        ),
        getDocs(
          query(
            collection(db, `projects/${projectID}/sales/${saleID}/items`),
            where("brand", "==", packingSlip.data.vendor)
          )
        ),
        dispatch("GET_SALE", { projectID, saleID }),
        dispatch("GET_ORDER", { projectID, saleID, orderID }),
      ]);
      const reorderReceived = await loadReorderReceived(dispatch, {
        projectID,
        saleID,
        orderID,
        order,
        pendingSlip: { id: packingSlip.id, data: packingSlip.data },
      });

      const vendorItems = itemsSnap.docs.map((doc) => {
        const data = doc.data();
        return {
          quantity: data.quantity || 0,
          quantityReady:
            doc.id in itemReadyOverrides
              ? itemReadyOverrides[doc.id]
              : data.quantityReady || 0,
        };
      });
      const vendorFullyReceived = computeVendorFullyReceived({
        items: vendorItems,
      });

      const statusAcks = sale.data.status.acks || {};
      const ackIDs = new Set(acksSnap.docs.map((doc) => doc.id));
      ackIDs.add(packingSlip.ackID);
      const ackOverrides = {};
      ackIDs.forEach((id) => {
        ackOverrides[id] = Boolean(statusAcks[id]?.receivedOverride);
      });
      const { ackReceived, orderReceived } = computeOrderReceipt({
        ackIDs,
        orderID,
        vendorFullyReceived,
        reorderReceived,
        ackOverrides,
      });

      // When the whole vendor is received, project `true` across the vendor's
      // OTHER orders and their acks too (material can't be attributed to one
      // order). This never lowers anything — it only raises flags.
      if (vendorFullyReceived) {
        const ordersSnap = await getDocs(
          query(
            collection(db, `projects/${projectID}/sales/${saleID}/orders`),
            where("vendor", "==", packingSlip.data.vendor)
          )
        );
        const vendorOrderIDs = new Set(ordersSnap.docs.map((doc) => doc.id));
        vendorOrderIDs.add(orderID);
        vendorOrderIDs.forEach((id) => {
          orderReceived[id] = true;
        });
        Object.entries(statusAcks).forEach(([id, data]) => {
          if (data && vendorOrderIDs.has(data.orderID)) {
            ackReceived[id] = true;
          }
        });
      }

      const ackUpdates = buildReceivedStatusUpdates({
        packingSlipID: packingSlip.id,
        qcInspected: packingSlip.qcInspected,
        total: packingSlip.total,
        ackReceived,
        orderReceived,
        receivedComplete: packingSlip.receivedComplete,
        received: packingSlip.qcInspected
          ? undefined
          : slipReceivedUnits(packingSlip.data.items),
      });

      return dispatch("UPDATE_SALE_STATUS", {
        batch,
        projectID,
        saleID,
        changes: {
          ...changes,
          ...ackUpdates,
        },
      });
    }
  };

  if (!packingSlip.id) {
    // New Packing Slip
    const now = new Date();
    const uid = state.user.id;
    packingSlip.data.created = now;
    packingSlip.data.createdBy = uid;

    const ref = doc(
      collection(db, PackingSlip.getPath(projectID, saleID, orderID))
    );
    packingSlip.id = ref.id;

    // Handle Skip QC - auto-mark items as ready and set location to vehicle
    if (packingSlip.data.skipQC) {
      const sale = await dispatch("GET_SALE", { projectID, saleID });
      const ack = await dispatch("GET_ACK", {
        projectID,
        saleID,
        orderID,
        ackID: packingSlip.data.ackID,
      });
      // Filter sale items by vendor
      const saleItems = sale.items.filter(
        (item) => item.brand === ack.data.vendor
      );

      // Build items data with all items marked as ready
      const items = {};
      let totalIncluded = 0;
      let totalReady = 0;
      saleItems.forEach((item) => {
        const remaining = item.data.quantity - item.data.quantityReady;
        if (remaining > 0) {
          const location = packingSlip.data.crossDockVehicle
            ? {
                id: packingSlip.data.crossDockVehicle,
                bin: null,
                type: "vehicle",
                itemType: item.itemLocationItemType || "unit",
                quantity: remaining,
              }
            : null;
          items[item.id] = {
            included: remaining,
            ready: remaining,
            undeliverable: 0,
            backorder: 0,
            missing: 0,
            damage: 0,
            locations: location ? [location] : [],
          };
          totalIncluded += remaining;
          totalReady += remaining;
        }
      });

      packingSlip.data.items = items;
      packingSlip.data.total = {
        included: totalIncluded,
        ready: totalReady,
        undeliverable: 0,
        backorder: 0,
        missing: 0,
        damage: 0,
        screens: 0,
        hardware: 0,
      };
      packingSlip.data.qcInspected = true;
      packingSlip.data.locked = true;

      // Update sale items with new ready quantities and locations
      for (const item of saleItems) {
        const itemData = items[item.id];
        if (itemData && itemData.ready > 0) {
          const quantityReady = (item.data.quantityReady || 0) + itemData.ready;
          const itemLocation = item.itemLocation
            ? item.itemLocation.map((l) => l.data)
            : [];
          if (itemData.locations.length) {
            itemData.locations.forEach((location) => {
              const existingLocation = itemLocation.find(
                (l) =>
                  l.id === location.id &&
                  (l.bin === location.bin || (!l.bin && !location.bin))
              );
              if (existingLocation) {
                existingLocation.quantity += location.quantity;
              } else {
                itemLocation.push(location);
              }
            });
          }
          batch.update(doc(db, item.path), {
            quantityReady,
            [`itemLocations.${now.getTime()}`]: itemLocation,
            modified: now,
            modifiedBy: uid,
          });
        }
      }

      // Update sale delivery.ready
      await dispatch("UPDATE_SALE_STATUS", {
        batch,
        projectID,
        saleID,
        changes: {
          "status.delivery.ready":
            (sale.data.status.delivery.ready || 0) + totalReady,
        },
      });
    }

    await saveDocuments();
    batch.set(ref.withConverter(PackingSlip), packingSlip);

    // Lock Acknowledgement
    const ack = await dispatch("GET_ACK", {
      projectID,
      saleID,
      orderID,
      ackID: packingSlip.data.ackID,
    });
    if (!ack.locked) {
      await dispatch("UPDATE_ACK", {
        projectID,
        saleID,
        orderID,
        ackID: packingSlip.data.ackID,
        changes: { locked: true },
      });
    }

    await updateSale({
      [`status.packingSlips.${packingSlip.id}`]: { orderID },
    });
    await batch.commit();
  } else {
    // Existing Packing Slip
    const now = new Date();
    const uid = state.user.id;
    packingSlip.data.modified = now;
    packingSlip.data.modifiedBy = uid;
    const current = await dispatch("GET_PACKING_SLIP", {
      projectID,
      saleID,
      orderID,
      packingSlipID: packingSlip.id,
    });

    await saveDocuments(current.data);
    batch.update(
      doc(db, packingSlip.path).withConverter(PackingSlip),
      packingSlip.data
    );

    const isDirectShip = packingSlip.data.directShip;

    // Update Sale Items from Initial QC Report
    let saleUpdates = {};
    let laborUpdates = {};
    // `status.install.readyItems` belongs to the sale the LABOR is on, which for
    // a cross-sale install is not the sale that owns the item — the same
    // routing `UPDATE_SALE_ITEM_STATUS` applies to its `installDeltas`. Keyed by
    // labor sale so each one gets only its own share.
    const installReadyBySale = {};
    // Tracks the sum of clamped per-item ready deltas so sale-level counters
    // below use the actually-applied total instead of `packingSlip.data.total.ready`
    // (which reflects the QC report rather than what was persisted after
    // per-item clamping against `item.quantity`).
    let totalReadyAdded = 0;
    // Projected post-save `quantityReady` per item, so updateSale can judge
    // vendor-wide receipt completeness before this batch commits.
    const itemReadyOverrides = {};
    if (packingSlip.data.qcInspected && !current.data.qcInspected) {
      await Promise.all(
        packingSlip.items.map(async (item) => {
          const itemStatus = packingSlip.data.items[item.id];
          const reportedReady = itemStatus ? itemStatus.ready || 0 : 0;
          if (!reportedReady) return;

          const { newlyReady, quantityReady, itemLocation } =
            computeQcReadyUpdate({
              item: item.data,
              reportedReady,
              reportedLocations: itemStatus.locations,
              existingLocations: item.itemLocation.map((l) => l.data),
            });
          if (!newlyReady) return;

          const normalizedItemLocation = dedupeItemLocations(itemLocation);
          const changes = {
            quantityReady,
            [`itemLocations.${now.getTime()}`]: normalizedItemLocation,
            modified: now,
            modifiedBy: uid,
          };
          if (isDirectShip) {
            const quantityDelivered =
              (item.data.quantityDelivered || 0) + newlyReady;
            const quantityScheduled =
              (item.data.quantityScheduled || 0) + newlyReady;
            changes.quantityDelivered = quantityDelivered;
            changes.quantityScheduled = quantityScheduled;
            changes.fullyDelivered = item.data.quantity <= quantityDelivered;
            changes.fullyScheduled = item.data.quantity <= quantityScheduled;
            changes[`itemLocations.${now.getTime()}`] = [
              {
                id: "delivered",
                type: "final",
                itemType: item.itemLocationItemType,
                quantity: quantityReady,
              },
            ];
          }
          batch.update(doc(db, item.path), changes);
          itemReadyOverrides[item.id] = quantityReady;
          totalReadyAdded += newlyReady;
          if (item.data.labor) {
            // Cross-sale install: the installing labor may live on a LATER sale
            // in this project (sale B's labor installing sale A's material), so
            // its doc is not under this item's sale. `laborSaleID` records where
            // it lives; without honouring it the update below targets
            // `projects/<p>/sales/<itemSale>/labor/<id>`, which does not exist,
            // and the whole QC submit fails with NOT_FOUND.
            const laborSaleID = item.data.laborSaleID || saleID;
            installReadyBySale[laborSaleID] =
              (installReadyBySale[laborSaleID] || 0) + newlyReady;
            if (!(item.data.labor in laborUpdates)) {
              laborUpdates[item.data.labor] = { readyDelta: 0, laborSaleID };
            }
            laborUpdates[item.data.labor].readyDelta += newlyReady;
          }
        })
      );
      if (!isEmpty(laborUpdates)) {
        Object.entries(laborUpdates).forEach(([id, update]) => {
          batch.update(
            doc(db, SaleLabor.getPath(projectID, update.laborSaleID, id)),
            {
              "status.quantityReady": increment(update.readyDelta),
              modified: now,
              modifiedBy: uid,
            }
          );
        });
      }
      // Update sale delivery.ready and install.ready. Uses `totalReadyAdded`
      // (sum of clamped per-item deltas) rather than
      // `packingSlip.data.total.ready` so sale-level counters stay aligned
      // with the per-item `quantityReady` writes above when clamping
      // trimmed some of the QC-reported ready counts.
      const sale = await dispatch("GET_SALE", { projectID, saleID });
      saleUpdates["total.screens"] =
        sale.data.total.screens + packingSlip.data.total.screens;
      saleUpdates["total.hardware"] =
        sale.data.total.hardware + packingSlip.data.total.hardware;
      saleUpdates["status.delivery.ready"] =
        sale.data.status.delivery.ready + totalReadyAdded;
      if (installReadyBySale[saleID]) {
        saleUpdates["status.install.readyItems"] =
          sale.data.status.install.readyItems + installReadyBySale[saleID];
      }
      if (isDirectShip) {
        saleUpdates["status.delivery.complete"] =
          sale.data.status.delivery.complete + totalReadyAdded;
        saleUpdates["status.delivery.screensComplete"] =
          sale.data.status.delivery.screensComplete +
          packingSlip.data.total.screens;
        saleUpdates["status.delivery.hardwareComplete"] =
          sale.data.status.delivery.hardwareComplete +
          packingSlip.data.total.hardware;
        saleUpdates["status.delivery.scheduled"] =
          sale.data.status.delivery.scheduled + totalReadyAdded;
        saleUpdates["status.delivery.screensScheduled"] =
          sale.data.status.delivery.screensScheduled +
          packingSlip.data.total.screens;
        saleUpdates["status.delivery.hardwareScheduled"] =
          sale.data.status.delivery.hardwareScheduled +
          packingSlip.data.total.hardware;
      }

      // For CPU (customer-pickup) sales whose ship-to is a company warehouse,
      // QC receives stock directly into the pickup location and no
      // delivery-to-pickup event ever fires. Without mirroring here,
      // `status.pickup.*Ready` stays 0 while `status.delivery.*Ready` climbs,
      // so `Sale.hasPickupToSchedule` never turns true and the "Customer
      // Pick-Up" action never appears (sale 04300131A). Keep the pickup-ready
      // counters equal to their delivery-ready counterparts — the same
      // invariant `repairSale` section 3a enforces — so QC alone keeps
      // `pickup.ready` authoritative without a Repair Sale.
      if (isPickupAddressAtWarehouse(sale)) {
        const pickup = sale.data.status.pickup || {};
        const readyMirror = [
          ["ready", saleUpdates["status.delivery.ready"]],
          ["screensReady", sale.data.status.delivery.screensReady || 0],
          ["hardwareReady", sale.data.status.delivery.hardwareReady || 0],
        ];
        for (const [field, deliveryValue] of readyMirror) {
          if ((pickup[field] || 0) !== deliveryValue) {
            saleUpdates[`status.pickup.${field}`] = deliveryValue;
          }
        }
      }

      // Cross-sale install: apply each OTHER sale's install-ready share to its
      // own sale doc. `updateSale` below only ever writes this sale.
      for (const [laborSaleID, readyDelta] of Object.entries(
        installReadyBySale
      )) {
        if (laborSaleID === saleID || !readyDelta) continue;
        batch.update(doc(db, Sale.getPath(projectID, laborSaleID)), {
          "status.install.readyItems": increment(readyDelta),
          modified: now,
          modifiedBy: uid,
        });
      }
    }

    await updateSale(saleUpdates, itemReadyOverrides);
    await batch.commit();
  }
  return packingSlip;
};

export const DELETE_PACKING_SLIP = async (
  { dispatch },
  { projectID, saleID, orderID, packingSlipID }
) => {
  const packingSlip = await dispatch("GET_PACKING_SLIP", {
    projectID,
    saleID,
    orderID,
    packingSlipID,
  });
  if (!packingSlip) {
    return;
  }
  // Defensive guard: refuse to delete a packing slip once it has been
  // QC-inspected. Mirrors `PackingSlip.canBeDeletedBy` so backend/script
  // callers (and the DELETE_ACK cascade) can't sneak around the UI gate.
  // The QC pass writes per-item `quantityReady` / `itemLocations` and
  // sale-level counters that this action doesn't reverse — deleting the
  // slip would leave inflated quantities pinned with no source.
  if (packingSlip.data.qcInspected) {
    throw new Error("packingSlipQcInspected");
  }

  const order = await dispatch("GET_ORDER", { projectID, saleID, orderID });

  // Recompute receipt completeness from the vendor's item quantities, OR the
  // per-ack manual `receivedOverride`, OR the re-order's own issue quantities.
  // Deleting a non-QC'd slip doesn't change item quantities, so a vendor that
  // was already fully received stays received; the per-slip
  // `receivedComplete` flag is not an input. The order is the AND of its acks.
  const [itemsSnap, sale, reorderReceived] = await Promise.all([
    getDocs(
      query(
        collection(db, `projects/${projectID}/sales/${saleID}/items`),
        where("brand", "==", packingSlip.data.vendor)
      )
    ),
    dispatch("GET_SALE", { projectID, saleID }),
    loadReorderReceived(dispatch, {
      projectID,
      saleID,
      orderID,
      order,
      excludeSlipID: packingSlipID,
    }),
  ]);
  const vendorFullyReceived = computeVendorFullyReceived({
    items: itemsSnap.docs.map((doc) => {
      const data = doc.data();
      return {
        quantity: data.quantity || 0,
        quantityReady: data.quantityReady || 0,
      };
    }),
  });
  const statusAcks = sale.data.status.acks || {};
  const ackIDs = order.acks.map((ack) => ack.id);
  const ackOverrides = {};
  ackIDs.forEach((id) => {
    ackOverrides[id] = Boolean(statusAcks[id]?.receivedOverride);
  });
  const { ackReceived, orderReceived } = computeOrderReceipt({
    ackIDs,
    orderID,
    vendorFullyReceived,
    reorderReceived,
    ackOverrides,
  });

  await deleteDoc(
    doc(db, PackingSlip.getPath(projectID, saleID, orderID, packingSlipID))
  );
  return dispatch("UPDATE_SALE_STATUS", {
    projectID,
    saleID,
    changes: {
      [`status.packingSlips.${packingSlipID}`]: deleteField(),
      [`status.acks.${packingSlip.ackID}.fullyReceived`]:
        ackReceived[packingSlip.ackID] ?? false,
      [`status.orders.${orderID}.fullyReceived`]: orderReceived[orderID],
    },
  });
};

/**
 * Skip the QC inspection and take the received quantities at face value.
 *
 * This used to be handed straight to the `bypassQC` callable, which marked the
 * slip `qcInspected` and `locked` but never touched the sale items. The
 * material therefore stayed at `quantityReady: 0` while the slip became
 * un-QC-able and un-deletable (PackingSlip.canBeDeletedBy refuses inspected
 * slips) -- nothing downstream could schedule or deliver it, and there was no
 * way back short of a Repair Sale.
 *
 * Rather than reimplement readiness server-side, the bypass now writes the QC
 * report it implies and hands it to SAVE_PACKING_SLIP, which is the one place
 * that knows how to make material ready: per-item clamping via
 * computeQcReadyUpdate, itemLocations, labor rollups, direct-ship, the pickup
 * mirror, and vendor-received status. Bypassing QC and passing QC now differ
 * only in who decided the items were good.
 *
 * The callable keeps the parts that genuinely need the server: the follow-up
 * service lead and the notifications.
 */
export const BYPASS_QC = async (
  { dispatch },
  { projectID, saleID, orderID, packingSlipID }
) => {
  const current = await dispatch("GET_PACKING_SLIP", {
    projectID,
    saleID,
    orderID,
    packingSlipID,
  });

  if (!current) throw new Error("Packing slip not found");
  if (current.data.qcInspected) {
    throw new Error("Packing slip already inspected");
  }

  // Everything the store hands out is deep-frozen (SET/SET_MANY in
  // store/main/mutations/init.js), so the cached instance cannot be edited --
  // assigning to its `data` throws "Cannot assign to read only property". Build
  // a fresh one from a clone, the way the edit dialogs do.
  const packingSlip = new PackingSlip(
    projectID,
    saleID,
    orderID,
    current.id,
    cloneDeep(current.data)
  );

  // The slip's items map is not a QC report yet: truck receiving
  // (receivedItemLocations) and Vendor Receiving Mode (completeReceiving)
  // record only where each item went, with no `included`, and a slip entered
  // by hand has no map at all. Reading `included` straight off it passed
  // nothing, and every bypass locked its slip with no unit made ready.
  //
  // So an item counts the units receiving placed, and with no map every
  // outstanding item of the slip's vendor counts, at the slip's first
  // location -- what QC opens the slip with, and what Skip QC on a new slip
  // makes ready. Never more than the item still has to be made ready. A slip
  // with no unit location carried no units: a truck that brought only
  // screens or hardware is saved with an empty map too, and its vendor's
  // outstanding windows never came off it.
  //
  // PackingSlip's `items` getter resolves each ID against
  // `state.main.saleItems`, and SAVE_PACKING_SLIP's QC pass iterates it. The
  // bypass is triggered from the QC dashboard widget, where the sale may never
  // have been opened, so this read also puts the items there.
  const saleItems = keyBy(
    (await dispatch("GET_ALL_SALE_ITEMS", { projectID, saleID })) || [],
    "id"
  );
  const outstanding = (item) =>
    Math.max(0, (item.data.quantity || 0) - (item.data.quantityReady || 0));
  const placed = (entry) =>
    (entry.locations || []).reduce(
      (sum, location) => sum + (Number(location.quantity) || 0),
      0
    );
  let received = packingSlip.data.items || {};
  const first = (packingSlip.data.itemLocations || []).find(
    (l) => l.itemType !== "screen" && l.itemType !== "hardware"
  );
  if (isEmpty(received) && first) {
    received = {};
    Object.values(saleItems)
      .filter((item) => item.brand === packingSlip.data.vendor)
      .forEach((item) => {
        const included = outstanding(item);
        received[item.id] = {
          included,
          locations: [
            {
              id: first.id,
              bin: first.bin || null,
              type: first.type || "warehouse",
              itemType: item.itemLocationItemType,
              quantity: included,
            },
          ],
        };
      });
  }

  // Everything received and not already flagged undeliverable passes. Per-item
  // `locations` come from receiving and are left alone -- they are what
  // computeQcReadyUpdate distributes the ready quantity across.
  const items = {};
  let totalIncluded = 0;
  let totalReady = 0;
  let totalUndeliverable = 0;
  Object.entries(received).forEach(([itemID, item]) => {
    const saleItem = saleItems[itemID];
    if (!saleItem) return;
    const included = Math.min(
      item.included ?? placed(item),
      outstanding(saleItem)
    );
    if (included <= 0) return;
    const undeliverable = item.undeliverable || 0;
    const ready = Math.max(0, included - undeliverable);
    items[itemID] = {
      undeliverable: 0,
      backorder: 0,
      missing: 0,
      damage: 0,
      ...item,
      included,
      ready,
    };
    totalIncluded += included;
    totalReady += ready;
    totalUndeliverable += Math.min(undeliverable, included);
  });

  const sumIncluded = (list) =>
    (list || []).reduce((sum, entry) => sum + (entry.included || 0), 0);
  const screens = sumIncluded(packingSlip.data.screens);
  const hardware = sumIncluded(packingSlip.data.hardware);

  // Locking a slip with nothing on it to pass is the stranded state above:
  // inspected, un-QC-able, un-deletable, and nothing ready.
  if (isEmpty(items) && !screens && !hardware) {
    throw new Error("Nothing on this packing slip to bypass QC for");
  }

  packingSlip.data.items = items;
  packingSlip.data.total = {
    included: totalIncluded,
    ready: totalReady,
    undeliverable: totalUndeliverable,
    backorder: 0,
    missing: 0,
    damage: 0,
    screens,
    hardware,
  };
  packingSlip.data.skipQC = true;
  packingSlip.data.skipQCReason =
    "QC Bypassed - Service Lead created for post-delivery inspection";
  packingSlip.data.qcInspected = true;
  packingSlip.data.locked = true;

  await dispatch("SAVE_PACKING_SLIP", {
    projectID,
    saleID,
    orderID,
    packingSlip,
  });

  return call("bypassQC", { projectID, saleID, orderID, packingSlipID });
};

/**
 * Vendor Receiving Mode says whether anything is still pending and which truck
 * the packages came off; completeReceiving reads both. Dropping them here made
 * the function infer completeness from that flow's always-empty backorder list,
 * marking every partial receive complete, and left every slip without a truck.
 *
 * Each is forwarded only when the caller actually states it. Receiving Mode
 * sends none, and keeps the function's fallbacks: completeness from its real
 * backorder list, and the slip's truck left as it is.
 */
export const COMPLETE_RECEIVING = (
  context,
  {
    projectID,
    saleID,
    orderID,
    packingSlipID,
    receivedItems,
    backorderedItemIds,
    receivedComplete,
    truckNumber,
    stopID,
  }
) => {
  return call("completeReceiving", {
    projectID,
    saleID,
    orderID,
    packingSlipID,
    receivedItems,
    backorderedItemIds,
    ...(isBoolean(receivedComplete) ? { receivedComplete } : {}),
    ...(truckNumber ? { truckNumber } : {}),
    ...(stopID ? { stopID } : {}),
  });
};
